<template>
  <div>
    <validation-observer
      id="claim-edit"
      v-slot="claimFormObserver"
      slim
    >
      <form-section-highlight>
        <form-row :label="label" />

        <!--  DATE  -->
        <form-row
          :id="`i0_${stepId}_${uniqueId}_claim_claim-date`"
          class="claim-date"
          question="What date was the claim made on?"
        >
          <div class="mt-4">
            <component
              :is="'question-base-date'"
              :step-id="stepId"
              question-id="date-of-claim"
              :vid="`i0_${stepId}_${uniqueId}_claim_claim-date`"
              :form-observer="formObserver"
              :form-data="claimToEdit"
              :step-data="item"
              :custom-fields="{
                field:'claimDate'
              }"
              data-test="date_of_claim"
              :validation-rules="{
                field: {
                  dateOfIncident: {
                    itemType: 'incident',
                  },
                  checkFutureIncident: {
                    itemType: 'incident',
                  },
                }
              }"
              class="date-of-claim-section"
              @input="claimToEdit.claimDate = $event.claimDate"
            />
          </div>
        </form-row>

        <!--  TYPE  -->
        <form-row
          :id="`i1_${stepId}_${uniqueId}_claim_property-claim-type`"
          question="What type of claim was it?"
        >
          <validation-provider
            v-slot="{ errors, failed }"
            name="Property Claim Type"
            :vid="`i1_${stepId}_${uniqueId}_claim_property-claim-type`"
            :rules="{ claimTypeRequired: true, claimType: claimTypesCounters }"
            slim
          >
            <qz-select
              v-model="claimToEdit.propertyClaimType"
              :items="options.propertyClaimTypeItems"
              data-test="property_claim_type"
              placeholder="Select claim type"
              :error="failed"
              :error-messages="errors"
            />
          </validation-provider>
        </form-row>

        <!--  CAUSE  -->
        <form-row
          :id="`i2_${stepId}_${uniqueId}_claim_claim-cause`"
          question="What was the cause of the claim?"
        >
          <validation-provider
            v-slot="{ errors, failed }"
            name="Property Claim Cause"
            :vid="`i2_${stepId}_${uniqueId}_claim_claim-cause`"
            rules="listOptionRequired"
            slim
          >
            <qz-select
              v-model="claimToEdit.claimCause"
              :items="options.claimCauseItems"
              data-test="claim_cause"
              placeholder="Select cause of claim"
              :error="failed"
              :error-messages="errors"
            />
          </validation-provider>
        </form-row>

        <!--  STATUS  -->
        <form-row
          :id="`i3_${stepId}_${uniqueId}_claim_claim-status`"
          question="Current claim status"
        >
          <validation-provider
            v-slot="{ failed }"
            name="Property Claim Status"
            :vid="`i3_${stepId}_${uniqueId}_claim_claim-status`"
            rules="required"
            slim
          >
            <radio
              v-model="claimStatus"
              :name="`${uniqueId}-claim-status`"
              wide
              data-test="claim_status"
              column
              :error="failed"
              :items="options.claimStatusItems"
            />
          </validation-provider>
        </form-row>

        <!--  AMOUNT  -->
        <form-row
          v-if="!isUndeclaredClaim"
          :id="`i4_${stepId}_${uniqueId}_claim_claim-amount`"
          question="What was the claim amount?"
          :error="formObserver.validated && !claimFormObserver.valid"
        >
          <validation-provider
            v-slot="{ errors, failed }"
            name="Property Claim Amount"
            :vid="`i4_${stepId}_${uniqueId}_claim_claim-amount`"
            rules="claimAmount"
            slim
          >
            <currency-input
              v-model.number="claimToEdit.claimAmount"
              :maxlength="inputCharsLimiters.CLAIM_AMOUNT"
              hide-details="auto"
              :error="failed"
              data-test="claim_amount"
              :error-messages="errors"
              outlined
              prefix="£"
              placeholder="e.g. 250"
            />
          </validation-provider>

          <template #helpText>
            <p>
              If you can't remember the exact amount, please provide an approximate figure.
            </p>
            <p>
              If no claim was made for this property, please enter £1.
            </p>
          </template>
        </form-row>

        <!--  LOCATION  -->
        <form-row
          v-if="!isUndeclaredClaim"
          :id="`i5_${stepId}_${uniqueId}_claim_claim-location`"
          question="Was the claim made at this property?"
        >
          <validation-provider
            v-slot="{ failed }"
            name="Property Claim Location"
            :vid="`i5_${stepId}_${uniqueId}_claim_claim-location`"
            :rules="{ required: !isUndeclaredClaim }"
            slim
          >
            <radio
              v-model="claimToEdit.claimLocation"
              :name="`${uniqueId}-claim-location`"
              wide
              data-test="claim_location"
              :error="failed"
              :items="options.yesNo"
            />
          </validation-provider>
        </form-row>

        <!--  SUBMIT  -->
        <div class="row no-gutters justify-end mt-4">
          <qz-btn
            depressed
            x-large
            dark
            rounded
            data-test="save_claim_details_btn"
            color="navy-light"
            @click="save(claimFormObserver)"
          >
            Save claim details
            <v-icon right>
              mdi-content-save
            </v-icon>
          </qz-btn>
          <qz-btn
            color="primary"
            text
            rounded
            class="ml-2"
            data-test="cancel_claim_details_btn"
            x-large
            @click.prevent="clearItem"
          >
            Cancel
            <template v-if="isEdit">
              edit
            </template>
          </qz-btn>
        </div>
      </form-section-highlight>
    </validation-observer>

    <!-- HIDDEN FIELD TO CHECK EDITOR NOT OPENED -->
    <validation-provider
      v-slot="{ failed }"
      name="Form Open Claims"
      :vid="`${stepId}_form-open-claims`"
      :rules="{ is_not: { other: true } }"
    >
      <input
        :value="formOpen"
        type="hidden"
      >
      <div
        v-if="failed && formObserver.validated"
        class="error--text text-body-1 mt-4"
      >
        You have unsaved claim details. Please save or cancel these details before continuing.
      </div>
    </validation-provider>
  </div>
</template>

<script>
import {
  cloneDeep,
} from 'lodash';

import { getRangeOfYears } from '@/helpers/date';

import getStepOptions from '@/helpers/home/getStepOptions';

import useSubformValidation from '@/composables/useSubformValidation';

export default {
  name: 'form-claim',
  props: {
    uniqueId: {
      type: String,
      default: 'new',
    },
    index: {
      type: Number,
      default: null,
    },
    item: {
      type: Object,
      required: true,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    claimTypesCounters: {
      type: [Object, null],
      required: false,
      default: null,
    },
    formObserver: {
      type: Object,
      required: true,
    },
    stepId: {
      type: String,
      default: 'drivers',
    },
  },
  inject: [
    'inputCharsLimiters',
    'constants',
    'helpers',
  ],
  data () {
    return {
      formOpen: true,

      claimToEdit: cloneDeep(this.item),
    };
  },
  computed: {
    label () {
      return this.helpers.getSubformLabel('Claim', this.isEdit, this.index);
    },

    isUndeclaredClaim () {
      return this.claimToEdit.claimStatus === 'NC';
    },
    claimStatus: {
      get () {
        return this.claimToEdit.claimStatus;
      },
      set (status) {
        this.claimToEdit.claimStatus = status;

        if (status === 'NC') {
          this.claimToEdit.claimAmount = null;
          this.claimToEdit.claimLocation = false;
        }
      },
    },
  },
  methods: {
    async save (observer) {
      await this.saveSubform({
        observer,
        subformType: 'claim',
        quoteData: this.claimToEdit,
      });
    },
    clearItem () {
      this.$emit('clear');
    },
  },
  setup (props, context) {
    return {
      getRangeOfYears,
      options: getStepOptions(3),

      ...useSubformValidation(null, context),
    };
  },
};
</script>

<style lang='scss'>
.claim-date {
  flex-direction: column;

  .form-col {
    width: 100% !important;
  }

  .form-row.columns {
    display: block;
  }
}
</style>
